import { useEffect, useState } from "react";
import api, { apiError, API_BASE } from "@/lib/api";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/dates";
import { FileText, Download, Sparkles, Trash2, Cloud, PlayCircle, TrendingUp, TrendingDown } from "lucide-react";

const KIND_LABELS = {
  executive_summary: "Executive Summary",
  risk_register: "Risk Register",
  accepted_risks: "Accepted Risks",
  coverage: "Framework Coverage",
  board_pack: "Board Pack (bundle)",
};

export default function Reports() {
  const [snapshots, setSnapshots] = useState([]);
  const [m365Scans, setM365Scans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("executive_summary");
  const [clearing, setClearing] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";

  const load = async () => {
    setLoading(true);
    try {
      const [snap, m365] = await Promise.all([
        api.get("/reports/snapshots").then((r) => r.data).catch(() => []),
        api.get("/m365/scans").then((r) => r.data).catch(() => []),
      ]);
      setSnapshots(snap);
      setM365Scans(m365);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const generate = async () => {
    try {
      await api.post("/reports/snapshot", { kind });
      toast.success("Snapshot generated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/reports/snapshots");
      toast.success(`Cleared ${data.deleted} report${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setClearing(false); }
  };

  const download = async (sid, fmt, kind) => {
    try {
      const token = localStorage.getItem("sq_token");
      const res = await fetch(`${API_BASE}/reports/snapshots/${sid}/export?fmt=${fmt}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${kind}-${sid.slice(0, 8)}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        kicker="Reports"
        title="Reports"
        description="Snapshot-based reporting. Regenerating an old snapshot reproduces identical output."
        actions={isAdmin && snapshots.length > 0 ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800" data-testid="btn-clear-reports">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear reports
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="clear-reports-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all report snapshots?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete every snapshot for your tenant ({snapshots.length} total). This action is audit-logged and cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="clear-reports-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={clearAll}
                  disabled={clearing}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="clear-reports-confirm"
                >
                  {clearing ? "Clearing…" : "Yes, delete all"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
      />
      <Card className="p-5 mb-6 border-cyan-200 bg-cyan-50/20">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Generate snapshot</div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-[240px]" data-testid="report-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(KIND_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={generate} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="btn-generate-snapshot"><Sparkles className="h-4 w-4 mr-1.5" /> Generate</Button>
        </div>
      </Card>

      {loading ? <LoadingSkeleton /> : snapshots.length === 0 ? (
        <EmptyState icon={FileText} title="No snapshots yet" description="Generate your first snapshot to unlock PDF/DOCX/XLSX exports." />
      ) : (
        <div className="space-y-3">
          {snapshots.map((s) => (
            <Card key={s.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`snapshot-${s.id.slice(0, 8)}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">{KIND_LABELS[s.kind]}</div>
                <div className="font-display font-semibold text-slate-900 mt-1">{s.title}</div>
                <div className="text-xs text-slate-500 mt-1 font-mono">by {s.created_by} · {formatDateTime(s.created_at)}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" onClick={() => download(s.id, "pdf", s.kind)} data-testid={`dl-pdf-${s.id.slice(0, 8)}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                <Button size="sm" variant="outline" onClick={() => download(s.id, "docx", s.kind)} data-testid={`dl-docx-${s.id.slice(0, 8)}`}><Download className="h-3.5 w-3.5 mr-1" /> DOCX</Button>
                <Button size="sm" variant="outline" onClick={() => download(s.id, "xlsx", s.kind)} data-testid={`dl-xlsx-${s.id.slice(0, 8)}`}><Download className="h-3.5 w-3.5 mr-1" /> XLSX</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* M365 Assessment reports */}
      {m365Scans.length > 0 && (
        <div className="mt-10" data-testid="m365-reports-section">
          <div className="flex items-center gap-2 mb-3">
            <Cloud className="h-4 w-4 text-cyan-700" />
            <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">M365 CIS Assessments</div>
            <span className="text-[11px] text-slate-500">Every scan produces an Executive PDF + a technical findings CSV.</span>
          </div>
          <div className="space-y-3">
            {m365Scans.map((s) => {
              const pct = Math.round(s.overall_hardening_pct || 0);
              const trend = pct >= 85 ? "green" : pct >= 60 ? "amber" : "red";
              const colors = { green: "text-emerald-700", amber: "text-amber-700", red: "text-rose-700" }[trend];
              return (
                <Card key={s.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`m365-report-${s.id.slice(0, 8)}`}>
                  <div className="h-11 w-11 rounded-xl bg-cyan-50 flex items-center justify-center">
                    <Cloud className="h-5 w-5 text-cyan-700" />
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">M365 · CIS v{s.cis_version}</div>
                    <div className="font-display font-semibold text-slate-900 mt-1">
                      {(s.org && s.org.display_name) || "Microsoft 365 tenant"} · <span className={colors}>{pct}% hardened</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">
                      {formatDateTime(s.created_at)} · {s.totals?.pass || 0}✓ · {s.totals?.fail || 0}✗ · {s.totals?.manual || 0} manual
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => download(s.id, "pdf", "m365-executive")} data-testid={`m365-dl-pdf-${s.id.slice(0, 8)}`}>
                      <Download className="h-3.5 w-3.5 mr-1" /> Executive PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      // Direct file download from the m365 export endpoints
                      (async () => {
                        try {
                          const token = localStorage.getItem("sq_token");
                          const r = await fetch(`${API_BASE}/m365/scans/${s.id}/export.csv`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                          const blob = await r.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a"); a.href = url; a.download = `m365-findings-${s.id.slice(0, 8)}.csv`; a.click();
                          URL.revokeObjectURL(url);
                        } catch { toast.error("CSV export failed"); }
                      })();
                    }} data-testid={`m365-dl-csv-${s.id.slice(0, 8)}`}>
                      <Download className="h-3.5 w-3.5 mr-1" /> Findings CSV
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
